import React, { createContext, useEffect, useState } from 'react';
import {
  type AuthUser,
  getUser,
  getToken,
  onAuthChange,
  fetchMe,
  signOut as authSignOut,
} from '../lib/auth';

interface AuthContextType {
  user: AuthUser | null;
  /** JWT access token when signed in */
  session: { access_token: string } | null;
  loading: boolean;
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);
export { AuthContext };

const DEV_BYPASS_AUTH = import.meta.env.VITE_DEV_BYPASS_AUTH === 'true';

const MOCK_USER: AuthUser = {
  id: 'dev-user',
  email: 'dev@macrodash.in',
  created_at: new Date().toISOString(),
};

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<AuthUser | null>(
    DEV_BYPASS_AUTH ? MOCK_USER : getUser()
  );
  const [session, setSession] = useState<{ access_token: string } | null>(() => {
    if (DEV_BYPASS_AUTH) return { access_token: 'dev-bypass' };
    const token = getToken();
    return token ? { access_token: token } : null;
  });
  const [loading, setLoading] = useState(DEV_BYPASS_AUTH ? false : true);

  useEffect(() => {
    if (DEV_BYPASS_AUTH) return;

    const unsub = onAuthChange((nextUser) => {
      setUser(nextUser);
      const token = getToken();
      setSession(token ? { access_token: token } : null);
      setLoading(false);
    });

    // Validate cached token against API
    fetchMe()
      .then((me) => {
        setUser(me);
        const token = getToken();
        setSession(token && me ? { access_token: token } : null);
      })
      .finally(() => setLoading(false));

    return unsub;
  }, []);

  const signOut = async () => {
    if (DEV_BYPASS_AUTH) return;
    await authSignOut();
    setUser(null);
    setSession(null);
  };

  const value: AuthContextType = {
    user,
    session,
    loading,
    signOut,
  };

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );
}
